'use client';

import { ButtonGreen } from 'components/buttons/default-green';

interface PricingCardProps {
  title: string;
  subtitle: string;
  isPopular?: boolean;
  features: {
    text: string;
    enabled: boolean;
  }[];
  ctaHref: string;
  ctaText?: string;
  usePhoneNumber?: boolean;
}

export function PricingCard({
  title,
  subtitle,
  isPopular = false,
  features = [],
  ctaHref = '#',
  ctaText = 'Shop Now',
  usePhoneNumber = false
}: PricingCardProps) {
  return (
    <div
      className={`relative rounded-[20px] bg-white p-2 ${isPopular ? 'lg:min-h-[640px]' : 'shadow-lg lg:min-h-[600px]'}`}
      style={
        isPopular
          ? {
              boxShadow: `
                    3px 4px 11px 0px rgba(1, 71, 55, 0.16),
                    14px 16px 21px 0px rgba(1, 71, 55, 0.14),
                    31px 35px 28px 0px rgba(1, 71, 55, 0.08),
                    55px 63px 33px 0px rgba(1, 71, 55, 0.02),
                    86px 98px 37px 0px rgba(1, 71, 55, 0)
                `
            }
          : undefined
      }
    >
      <div className={`flex h-full flex-col justify-between space-y-6`}>
        <div>
          <div
            className="relative rounded-xl p-4 text-white"
            style={{
              background: isPopular
                ? 'radial-gradient(47.42% 149.08% at 104.65% 0%, #43BC4C 0%, #28712D 100%)'
                : '#EBECF0'
            }}
          >
            {isPopular && (
              <div className="absolute left-4 top-4">
                <div className="flex items-center gap-1 rounded-md bg-green-900 px-3 py-1 text-xs text-white">
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 10 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clipPath="url(#clip0_15473_5297)">
                      <path
                        d="M4.96377 9.99694C4.20931 10.0241 3.46004 9.87009 2.78803 9.5497C2.11601 9.22931 1.54386 8.75334 1.12661 8.16756C-0.512247 5.59717 1.12661 3.17999 1.66605 2.50016L1.71805 2.43526C1.95405 2.2078 2.11935 1.925 2.19634 1.61698C2.27332 1.30897 2.2591 0.987286 2.15519 0.686203C2.12027 0.578902 2.12265 0.464072 2.16201 0.35811C2.20137 0.252148 2.27569 0.160472 2.37435 0.096173C2.47302 0.0318736 2.59098 -0.00176134 2.71141 7.10876e-05C2.83183 0.00190351 2.94855 0.0391095 3.04491 0.106376C3.12091 0.159572 3.22376 0.21915 3.34491 0.290963C4.20817 0.719933 4.94498 1.3398 5.49052 2.09606C6.03606 2.85231 6.37361 3.72177 6.47349 4.62796C6.75885 4.06391 6.91455 3.4506 6.93063 2.82731C6.93551 2.73114 6.96832 2.63801 7.02558 2.55784C7.08284 2.47767 7.1624 2.41347 7.25578 2.37208C7.34915 2.3307 7.45284 2.31367 7.55579 2.32283C7.65874 2.33199 7.75709 2.36699 7.84035 2.42409C8.77521 3.06509 10.4906 5.65888 8.99407 7.97818C8.56468 8.60821 7.96842 9.12482 7.2626 9.47836C6.55678 9.8319 5.76514 10.0105 4.96377 9.99694Z"
                        fill="white"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_15473_5297">
                        <rect width="10" height="10" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  Most Popular
                </div>
              </div>
            )}
            <div className={`${isPopular ? 'pt-10' : 'text-[#252725]'}`}>
              <h3 className="text-xl font-semibold">{title}</h3>
              <p className="text-sm opacity-90">{subtitle}</p>
            </div>
          </div>

          <div className="mt-6 space-y-4 px-4">
            <div className="flex items-center justify-center gap-4">
              <div className="h-[1px] w-full bg-[rgba(202,206,203,1)]" />
              <h4 className="whitespace-nowrap text-center font-medium text-[rgba(112,118,112,1)]">
                MAIN FEATURES
              </h4>
              <div className="h-[1px] w-full bg-[rgba(202,206,203,1)]" />
            </div>
            <div className="space-y-3">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  {feature.enabled ? (
                    <div className="min-w-[1.5rem]">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="h-5 w-5 text-fp-green"
                      >
                        <path
                          fillRule="evenodd"
                          d="M19.916 4.626a.75.75 0 0 1 .208 1.04l-9 13.5a.75.75 0 0 1-1.154.114l-6-6a.75.75 0 0 1 1.06-1.06l5.353 5.353 8.493-12.74a.75.75 0 0 1 1.04-.207Z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  ) : (
                    <div className="min-w-[1.5rem]">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="h-5 w-5 text-gray-400"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                  <span className="text-gray-600">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
          <div className="px-4">
            {!usePhoneNumber && (
              <ButtonGreen
                href={ctaHref}
                className={`w-full justify-center rounded-lg py-3 ${!isPopular ? 'bg-white !text-fp-green' : ''}`}
              >
                <div
                  className="flex items-center gap-2"
                  dangerouslySetInnerHTML={{ __html: ctaText }}
                />
              </ButtonGreen>
            )}

            {usePhoneNumber && (
              <ButtonGreen
                href={`tel:833-606-4477`}
                className="flex w-full flex-row items-center justify-center gap-[10px] rounded-lg border-[2px] border-fp-green bg-white px-4 py-2 text-sm text-fp-green"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="size-4"
                >
                  <path
                    fillRule="evenodd"
                    d="M1.5 4.5a3 3 0 0 1 3-3h1.372c.86 0 1.61.586 1.819 1.42l1.105 4.423a1.875 1.875 0 0 1-.694 1.955l-1.293.97c-.135.101-.164.249-.126.352a11.285 11.285 0 0 0 6.697 6.697c.103.038.25.009.352-.126l.97-1.293a1.875 1.875 0 0 1 1.955-.694l4.423 1.105c.834.209 1.42.959 1.42 1.82V19.5a3 3 0 0 1-3 3h-2.25C8.552 22.5 1.5 15.448 1.5 6.75V4.5Z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Call 833-606-4477</span>
              </ButtonGreen>
            )}
          </div>

          {usePhoneNumber && (
            <div className="mb-8 mt-3 space-y-1 px-4 text-center font-medium">
              <p className="text-sm text-gray-600">
                Plan is available through a personalized consultation with one of our security
                specialists.
              </p>
            </div>
          )}

          {!usePhoneNumber && (
            <div className="mb-8 mt-3 space-y-1 text-center font-medium">
              <p className="text-sm text-gray-600">For this plan and latest product deals</p>
              <a href="tel:833-606-4477" className="block text-fp-green underline">
                Call 833-606-4477
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
